import React from "react";
import { api, apiFetch, fetchAuthUsers } from "../services/api";
import {
  buildMergedAuditTimeline,
  enrichmentEventSubtitle,
  enrichmentEventTitle,
  entityAuditChangeTitle,
  formatRelationshipAuditLine,
  resolveAuditUserName,
} from "../utils/entityAuditTrail";

const formatLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

const ReadSectionPencil = ({ label, onClick }) => {
  if (typeof onClick !== "function") return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-blue-400 hover:bg-gray-700/80 transition-colors"
      aria-label={`Edit ${label}`}
      title={`Edit ${label}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="h-4 w-4"
        aria-hidden
      >
        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
      </svg>
    </button>
  );
};

const EntityAuditRow = ({
  entry,
  userDirectory,
  normalizeAuditJson,
  isMeaningfulAuditField,
}) => {
  if (entry.item_type === "lifecycle" && entry.lifecycle) {
    const L = entry.lifecycle;
    const meta = normalizeAuditJson(L.metadata) || {};
    const role = meta.role;
    const statusLine = [L.from_status, L.to_status].filter(Boolean).join(" → ");
    const actorName = resolveAuditUserName(entry.changed_by_user_id, userDirectory);

    return (
      <div
        key={entry.id}
        className="bg-gray-800 rounded-lg border border-gray-700 border-l-2 border-l-teal-600/80 px-3 py-2 text-sm"
      >
        <div className="flex items-center justify-between mb-1 gap-2">
          <div className="text-gray-200 min-w-0">
            <span className="font-medium text-teal-100/90">
              Extraction review: {entry.change_type}
              {L.item_kind ? ` · ${L.item_kind}` : ""}
            </span>{" "}
            <span className="text-xs text-gray-400">{entry.change_source}</span>
            {actorName ? (
              <span className="text-xs text-gray-500 ml-2">by {actorName}</span>
            ) : null}
          </div>
          <div className="text-xs text-gray-400 shrink-0">
            {formatWhen(entry.changed_at)}
          </div>
        </div>
        <div className="mt-1 text-xs text-gray-300 space-y-1">
          {statusLine && (
            <div>
              <span className="text-gray-400">Status: </span>
              {statusLine}
            </div>
          )}
          {role === "merge_target_canonical" && (
            <div>
              <span className="text-gray-400">Pending item merged in: </span>
              {meta.source_entity_id || "—"}
            </div>
          )}
          {entry.change_type === "merge_entities" &&
            role !== "merge_target_canonical" &&
            (meta.target_entity_id || meta.source_entity_id) && (
              <div className="text-gray-400 break-all">
                {meta.target_entity_id
                  ? `Target (canonical): ${meta.target_entity_id}`
                  : null}
                {meta.target_entity_id && meta.source_entity_id ? " · " : null}
                {meta.source_entity_id
                  ? `Source (merged away): ${meta.source_entity_id}`
                  : null}
              </div>
            )}
          {Object.keys(meta).length > 0 && (
            <details className="pt-0.5">
              <summary className="text-gray-500 cursor-pointer text-[11px]">
                Details
              </summary>
              <pre className="mt-1 text-[11px] text-gray-400 overflow-x-auto max-h-40 whitespace-pre-wrap break-all">
                {JSON.stringify(meta, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  const attrDiff = normalizeAuditJson(entry.attributes_diff);
  const relDiff = normalizeAuditJson(entry.relationships_diff);
  const changedAttrsRaw = attrDiff.changed || {};
  const changedAttrs = Object.fromEntries(
    Object.entries(changedAttrsRaw).filter(([key]) =>
      isMeaningfulAuditField(key)
    )
  );
  const addedAttrs = (attrDiff.added || []).filter((key) =>
    isMeaningfulAuditField(key)
  );
  const removedAttrs = (attrDiff.removed || []).filter((key) =>
    isMeaningfulAuditField(key)
  );
  const actorName = resolveAuditUserName(entry.changed_by_user_id, userDirectory);

  const formatVal = (v) =>
    typeof v === "object" && v !== null ? JSON.stringify(v) : String(v);

  const title = entityAuditChangeTitle(entry);

  const renderRelationshipList = (items, verb) =>
    (items || []).map((rel, index) => (
      <li key={`${verb}-${rel?.id || index}`}>
        {verb}: {formatRelationshipAuditLine(rel, formatLabel)}
      </li>
    ));

  return (
    <div
      key={entry.id}
      className="bg-gray-800 rounded-lg border border-gray-700 px-3 py-2 text-sm"
    >
      <div className="flex items-center justify-between mb-1 gap-2">
        <div className="text-gray-200 min-w-0">
          <span className="font-medium">{title}</span>{" "}
          <span className="text-xs text-gray-400">{entry.change_source}</span>
          {actorName ? (
            <span className="text-xs text-gray-500 ml-2">by {actorName}</span>
          ) : null}
        </div>
        <div className="text-xs text-gray-400 shrink-0">
          {formatWhen(entry.changed_at)}
        </div>
      </div>

      <div className="mt-1 text-xs text-gray-300 space-y-1">
        {entry.change_type === "delete" && (
          <div className="text-gray-400 text-xs">
            This item was removed from the map.
          </div>
        )}

        {Object.keys(changedAttrs).length > 0 && (
          <div>
            <div className="text-gray-400">Changed fields:</div>
            <ul className="list-disc list-inside space-y-0.5">
              {Object.entries(changedAttrs).map(([key, change]) => (
                <li key={key}>
                  <span className="font-medium">{formatLabel(key)}:</span>{" "}
                  {formatVal(change.before)} → {formatVal(change.after)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {addedAttrs.length > 0 && (
          <div>
            <span className="text-gray-400">Added fields:</span>{" "}
            {addedAttrs.map(formatLabel).join(", ")}
          </div>
        )}

        {removedAttrs.length > 0 && (
          <div>
            <span className="text-gray-400">Removed fields:</span>{" "}
            {removedAttrs.map(formatLabel).join(", ")}
          </div>
        )}

        {(relDiff.added?.length ||
          relDiff.removed?.length ||
          relDiff.changed?.length) && (
          <div className="pt-1">
            <div className="text-gray-400">Relationship changes:</div>
            <ul className="list-disc list-inside space-y-0.5">
              {renderRelationshipList(relDiff.added, "Added")}
              {renderRelationshipList(relDiff.removed, "Removed")}
              {(relDiff.changed || []).map((change, index) => {
                const before = change?.before || {};
                const after = change?.after || {};
                const beforeLabel = formatRelationshipAuditLine(before, formatLabel);
                const afterLabel = formatRelationshipAuditLine(after, formatLabel);
                return (
                  <li key={`changed-${before?.id || index}`}>
                    Updated: {beforeLabel} → {afterLabel}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const EnrichmentAuditRow = ({ ev, userDirectory, rowKey }) => {
  const title = enrichmentEventTitle(ev);
  const subtitle = enrichmentEventSubtitle(ev);
  const actorName = resolveAuditUserName(ev?.actor_user_id, userDirectory);

  return (
    <div
      key={rowKey}
      className="bg-gray-800 rounded-lg border border-gray-700 border-l-2 border-l-violet-600/70 px-3 py-2 text-sm"
    >
      <div className="flex items-center justify-between mb-1 gap-2">
        <div className="text-gray-200 min-w-0">
          <span className="font-medium text-violet-300">{title}</span>
          <span className="text-xs text-gray-400 ml-2">AI enrichment review</span>
          {actorName ? (
            <span className="text-xs text-gray-500 ml-2">by {actorName}</span>
          ) : null}
        </div>
        <div className="text-xs text-gray-400 shrink-0">{formatWhen(ev?.at)}</div>
      </div>
      {subtitle ? (
        <p className="mt-1 text-xs text-gray-300">{subtitle}</p>
      ) : null}
      {ev?.job_display_name ? (
        <p className="mt-1 text-xs text-gray-500">Job: {ev.job_display_name}</p>
      ) : null}
    </div>
  );
};

/**
 * Unified entity audit trail: version history, lifecycle, relationships, and enrichment review.
 */
const EntityAuditTrailSection = ({
  entityId,
  userDirectory,
  setUserDirectory,
  normalizeAuditJson,
  isMeaningfulAuditField,
  onRequestEdit,
  onEditHistory,
  /** Increment to reload audit entries after entity/relationship saves. */
  refreshToken = 0,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [entityEntries, setEntityEntries] = React.useState([]);
  const [enrichmentEntries, setEnrichmentEntries] = React.useState([]);

  const eid = String(entityId || "").trim();

  React.useEffect(() => {
    setExpanded(false);
    setLoaded(false);
    setLoading(false);
    setError(null);
    setEntityEntries([]);
    setEnrichmentEntries([]);
  }, [eid]);

  const enrichUserDirectory = React.useCallback(
    async (_entityRows, _enrichmentRows) => {
      if (typeof setUserDirectory !== "function") return;

      const mergeUsers = (users) => {
        if (!Array.isArray(users) || !users.length) return;
        setUserDirectory((prev) => {
          const next = { ...prev };
          users.forEach((u) => {
            if (!u?.id) return;
            next[u.id] = u.name || u.email || u.id;
          });
          return next;
        });
      };

      try {
        const meRes = await apiFetch("/api/auth/me", { method: "GET" });
        if (meRes?.ok) {
          const me = await meRes.json();
          if (me?.id) mergeUsers([me]);
        }
      } catch {
        // ignore
      }

      try {
        mergeUsers(await fetchAuthUsers());
      } catch {
        // Non-admins cannot list org users.
      }

      try {
        const allRes = await apiFetch("/api/admin/all-users", { method: "GET" });
        if (allRes?.ok) {
          const allUsers = await allRes.json();
          mergeUsers(Array.isArray(allUsers) ? allUsers : []);
        }
      } catch {
        // Super-user directory optional.
      }
    },
    [setUserDirectory]
  );

  React.useEffect(() => {
    if (!expanded || !eid) return;
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [auditRes, enrichmentData] = await Promise.all([
          apiFetch(`/api/entities/${eid}/audit?_t=${refreshToken}`, {
            method: "GET",
            cache: "no-store",
          }),
          api.getEntityEnrichmentReviewAudit(eid).catch(() => ({ items: [] })),
        ]);

        if (!isMounted) return;

        let entityRows = [];
        if (auditRes?.ok) {
          const data = await auditRes.json();
          entityRows = data.items || [];
        } else if (auditRes) {
          const err = await auditRes.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to load audit trail.");
        }

        const enrichmentRows = Array.isArray(enrichmentData?.items)
          ? enrichmentData.items
          : [];

        setEntityEntries(entityRows);
        setEnrichmentEntries(enrichmentRows);
        setLoaded(true);
        void enrichUserDirectory(entityRows, enrichmentRows);
      } catch (e) {
        if (!isMounted) return;
        setError(e?.message || "Failed to load audit trail.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void load();
    return () => {
      isMounted = false;
    };
  }, [eid, expanded, refreshToken, enrichUserDirectory]);

  const timeline = React.useMemo(
    () =>
      buildMergedAuditTimeline(entityEntries, enrichmentEntries, {
        normalizeAuditJson,
        isMeaningfulAuditField,
      }),
    [entityEntries, enrichmentEntries, normalizeAuditJson, isMeaningfulAuditField]
  );

  if (!eid) return null;

  const countLabel = () => {
    if (loading) return "Loading…";
    if (!loaded) return "Expand to load";
    if (!timeline.length) return "No events";
    return `${timeline.length} event${timeline.length === 1 ? "" : "s"}`;
  };

  return (
    <section className="rounded-lg border border-gray-700/80 bg-gray-800/30">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left border-b border-gray-700/60"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="text-sm font-semibold text-white">Audit trail</span>
        <span className="text-xs text-gray-400 shrink-0">
          {countLabel()}
          <span className="ml-2">{expanded ? "▾" : "▸"}</span>
        </span>
      </button>

      {expanded ? (
        <div className="px-3 py-3">
          <div className="flex justify-end -mt-1 mb-2">
            <ReadSectionPencil
              label="audit trail"
              onClick={
                typeof onRequestEdit === "function" ? onEditHistory : undefined
              }
            />
          </div>
          {loading && timeline.length > 0 && (
            <div className="text-gray-500 text-xs mb-2">Refreshing…</div>
          )}
          {loading && timeline.length === 0 && (
            <div className="text-gray-400 text-sm">Loading audit trail…</div>
          )}
          {error && !loading && (
            <div className="text-red-400 text-sm">{error}</div>
          )}
          {!loading && !error && timeline.length === 0 && (
            <div className="text-gray-500 text-sm">
              No audit events recorded for this entity yet.
            </div>
          )}
          {!error && timeline.length > 0 && (
            <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
              {timeline.map((item, idx) => {
                if (item.kind === "enrichment") {
                  const rowKey = `enrich-${item.at}-${item.event?.action || ""}-${idx}`;
                  return (
                    <EnrichmentAuditRow
                      key={rowKey}
                      rowKey={rowKey}
                      ev={item.event}
                      userDirectory={userDirectory}
                    />
                  );
                }
                return (
                  <EntityAuditRow
                    key={item.entry?.id || `entity-${item.at}-${idx}`}
                    entry={item.entry}
                    userDirectory={userDirectory}
                    normalizeAuditJson={normalizeAuditJson}
                    isMeaningfulAuditField={isMeaningfulAuditField}
                  />
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
};

export default EntityAuditTrailSection;
